import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from "react";
import useAIStreaming, { SSEEvent } from "features/ai/hooks/useAIStreaming";
import { aiService } from "Apis";
import {
  ApiAISession,
  ApiAIPrompt,
  ApiAIPromptStatus,
  ApiAIPromptType,
} from "@incendium/api";
import { useAISessions } from "../hooks/useAISessions";
import { produce } from "immer";
import { useSelectedProject } from "Hooks";
import { usePrevious } from "react-use";
import useAppendSearchState from "Hooks/useAppendSearchState";

enum ROUTES {
  RouteChartBuilder = "chart_builder",
  RouteChartBuilderAndAnalyst = "chart_builder_&_analyst",
  RoutePlanner = "planner",
  RouteAnalyst = "analyst",
  RouteService = "service",
  RouteMCPAgent = "mcp_agent",
}

const routeToResponse: Record<string, string> = {
  [ROUTES.RouteChartBuilder]: "Building your Chart, One moment please",
  [ROUTES.RouteChartBuilderAndAnalyst]:
    "I will help you build a chart and analyse it for you, One moment please",
  [ROUTES.RouteAnalyst]: "I will help you analyse the chart for you",
  [ROUTES.RouteService]: "I will help you with your request",
  [ROUTES.RoutePlanner]: "I will help you with your deep dive",
  [ROUTES.RouteMCPAgent]: "",
};

export const chatId = "chatID";

interface AiContextType {
  // Modal state
  active: boolean;
  setActive: (active: boolean) => void;

  // Current question (for text field)
  currentQuestion: string;
  setCurrentQuestion: (question: string) => void;

  // Sessions and messages
  aiSessions: ApiAISession[];
  currentSession: ApiAISession | null;
  messages: ApiAIPrompt[];
  loadSession: (sessionId: string) => void;
  // Session management
  createSessionAndStream: (prompt: string) => Promise<void>;
  addResponse: (message: Omit<ApiAIPrompt, "id" | "timestamp">) => string;
  updateMessage: (messageId: string, updates: Partial<ApiAIPrompt>) => void;

  // Streaming state
  isStreaming: boolean;
  isConnecting: boolean;
  streamingError: string | null;
  stopStreaming: () => Promise<void>;

  hasFailedPrompt: boolean;

  retryPrompt: (messageId?: string) => void;

  // Clear state
  clearCurrentSession: () => void;

  /** Super-admin only: route the next message through the MCP agent. */
  useMcpAgent: boolean;
  setUseMcpAgent: (v: boolean) => void;
  mcpModeLocked: boolean;
}

export const AiContext = createContext<AiContextType>({
  active: false,
  setActive: () => {},
  currentQuestion: "",
  setCurrentQuestion: () => {},
  aiSessions: [],
  currentSession: null,
  loadSession: (sessionId: string) => {},
  messages: [],
  createSessionAndStream: async () => {},
  addResponse: () => "",
  updateMessage: () => {},
  isStreaming: false,
  isConnecting: false,
  streamingError: null,
  stopStreaming: async () => {},
  hasFailedPrompt: false,
  clearCurrentSession: () => {},
  retryPrompt: () => {},
  useMcpAgent: true,
  setUseMcpAgent: () => {},
  mcpModeLocked: false,
});

export const useAiContext = () => {
  return useContext(AiContext);
};

export const AiProvider = ({ children }: { children: React.ReactNode }) => {
  const [active, setActive] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const { aiSessions, setAiSessions } = useAISessions();
  const [currentSession, setCurrentSession] = useState<ApiAISession | null>(
    null,
  );
  const [isStreaming, setIsStreaming] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [streamingError, setStreamingError] = useState<string | null>(null);
  const { selectedProject } = useSelectedProject();
  const [useMcpAgent, setUseMcpAgent] = useState(true);
  const mcpModeLocked = !!currentSession?.id;

  // URL-based session management
  const [urlSessionId, setUrlSessionId] = useAppendSearchState<string>(
    chatId,
    null,
  );

  // Refs for streaming
  const currentApiAIPromptRef = useRef<string | null>(null);
  const currentContentRef = useRef<string>("");
  const currentSessionIdRef = useRef<string | null>(null);
  const currentSessionRef = useRef<ApiAISession | null>(null);

  // Generate unique IDs
  const generateId = useCallback(() => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Get messages from current session
  const messages = useMemo(() => {
    // each prompt has user input and response, we need t split them into seperate messages
    // if the type is AIPromptTypeChart, we ignore the
    const arr = (currentSession?.prompts || [])
      .map((prompt) => {
        let msg: ApiAIPrompt[] = [];
        if (
          ![
            ApiAIPromptType.AI_PROMPT_TYPE_CHART,
            ApiAIPromptType.AI_PROMPT_TYPE_SERVICE,
            ApiAIPromptType.AI_PROMPT_TYPE_PLANNER,
            ApiAIPromptType.AI_PROMPT_TYPE_MCP_AGENT,
          ].includes(prompt.type as ApiAIPromptType)
        ) {
          msg.push({
            ...prompt,
            userInput: prompt.userInput || "",
            response: "",
          });
        }

        if (prompt.response !== "") {
          let response = prompt.response || "";
          if (prompt.type === ApiAIPromptType.AI_PROMPT_TYPE_ROUTER) {
            response = routeToResponse[response] ?? response;
          }
          msg.push({
            ...prompt,
            userInput: "",
            response: response,
          });
        } else if (
          prompt.status === ApiAIPromptStatus.AI_PROMPT_STATUS_FAILED
        ) {
          msg.push({
            ...prompt,
            userInput: "",
            response: "Sorry, there was an error processing your request.",
          });
        } else if (
          prompt.status === ApiAIPromptStatus.AI_PROMPT_STATUS_CANCELLED
        ) {
          msg.push({
            ...prompt,
            userInput: "",
            response: prompt.response || "",
          });
        }
        return msg;
      })
      .flat()
      .filter(
        (msg) =>
          msg.response !== "" ||
          msg.userInput !== "" ||
          msg.status === ApiAIPromptStatus.AI_PROMPT_STATUS_CANCELLED,
      );
    return arr;
  }, [currentSession?.prompts]);

  const hasFailedPrompt = useMemo(() => {
    return (
      currentSession?.prompts?.some(
        (msg) => msg.status === ApiAIPromptStatus.AI_PROMPT_STATUS_FAILED,
      ) || false
    );
  }, [currentSession?.prompts]);

  const addResponse = useCallback(
    (message: Omit<ApiAIPrompt, "id">): string => {
      const newMessage: ApiAIPrompt = {
        ...message,
        id: generateId(),
        createdAt: new Date(),
      };

      setAiSessions(
        aiSessions.map((session) => {
          if (session.id === currentSession?.id) {
            return {
              ...session,
              prompts: [...(session.prompts || []), newMessage],
            };
          }
          return session;
        }),
      );

      setCurrentSession((prev) => {
        if (!prev) return null;
        const updated = {
          ...prev,
          prompts: [...(prev.prompts || []), newMessage],
        };
        currentSessionRef.current = updated;
        return updated;
      });

      return newMessage.id!;
    },
    [currentSession?.id, generateId, aiSessions],
  );

  const updateMessage = useCallback(
    (messageId: string, updates: Partial<ApiAIPrompt>) => {
      setAiSessions(
        aiSessions.map((session) => {
          if (session.id === currentSession?.id) {
            return {
              ...session,
              prompts: (session.prompts || []).map((msg) =>
                msg.id === messageId ? { ...msg, ...updates } : msg,
              ),
            };
          }
          return session;
        }),
      );

      setCurrentSession((prev) => {
        if (!prev) return null;
        const updated = {
          ...prev,
          prompts: (prev.prompts || []).map((msg) =>
            msg.id === messageId ? { ...msg, ...updates } : msg,
          ),
        };
        currentSessionRef.current = updated;
        return updated;
      });
    },
    [currentSession?.id, aiSessions],
  );

  const clearCurrentSession = useCallback(() => {
    setCurrentSession(null);
    setCurrentQuestion("");
    setIsStreaming(false);
    setIsConnecting(false);
    setStreamingError(null);
    currentApiAIPromptRef.current = null;
    currentContentRef.current = "";
    currentSessionIdRef.current = null;
    currentSessionRef.current = null;
    setUrlSessionId(null); // Clear the URL parameter
  }, [setUrlSessionId]);

  const loadSession = useCallback(
    async (sessionId: string, dontClearCurrentSession = false) => {
      if (currentSessionIdRef.current === sessionId) return;
      if (!dontClearCurrentSession) {
        clearCurrentSession();
      }

      let foundSession = aiSessions.find((session) => session.id === sessionId);
      if (foundSession?.id) {
        const res = await aiService.aiServiceListSessionPrompts({
          projectId: selectedProject?.id!,
          sessionId: foundSession.id!,
        });
        if (!res.prompts) {
          throw new Error("Failed to load session prompts");
        }

        const session = {
          ...foundSession,
          prompts: res.prompts,
        };
        currentSessionIdRef.current = session.id!;
        setCurrentSession(session);
        setUseMcpAgent(!!session.useMcpAgent);
        currentSessionRef.current = session;
      }
    },
    [aiSessions, clearCurrentSession, selectedProject?.id],
  );

  // Streaming handlers
  const handleSSEMessage = useCallback(
    (event: SSEEvent) => {
      if (!event.data) return;

      const messageId = currentApiAIPromptRef.current;
      const sessionId = currentSessionIdRef.current;
      if (!messageId || !sessionId) return;

      currentContentRef.current += event.data;

      // Use functional updaters to avoid stale closures.
      setAiSessions((prev) =>
        produce(prev, (draft) => {
          const s = draft.find((x) => x.id === sessionId);
          if (!s || !s.prompts) return;
          s.prompts = s.prompts.map((p) =>
            p.id === messageId
              ? { ...p, response: currentContentRef.current }
              : p,
          );
        }),
      );

      setCurrentSession((prev) => {
        if (!prev || prev.id !== sessionId) return prev;
        const updated = {
          ...prev,
          prompts: (prev.prompts || []).map((p) =>
            p.id === messageId
              ? { ...p, response: currentContentRef.current }
              : p,
          ),
        };
        currentSessionRef.current = updated;
        return updated;
      });
    },
    [setAiSessions, setCurrentSession],
  );

  const handleSSEError = useCallback(
    (error: string) => {
      setIsStreaming(false);
      setIsConnecting(false);
      setStreamingError(error);

      // Add error message if we have an active AI message
      if (currentApiAIPromptRef.current) {
        updateMessage(currentApiAIPromptRef.current, {
          response:
            "Sorry, there was an error processing your request. Please try again.",
          status: ApiAIPromptStatus.AI_PROMPT_STATUS_FAILED,
        });
      }
      currentApiAIPromptRef.current = null;
    },
    [updateMessage],
  );

  const handleSSEClose = useCallback(async () => {
    setIsStreaming(false);
    setIsConnecting(false);

    // refetch session prompts and amend the last one
    const res = await aiService.aiServiceListSessionPrompts({
      projectId: selectedProject?.id!,
      sessionId: currentSessionIdRef.current!,
    });
    if (!res.prompts) {
      throw new Error("Failed to load session prompts");
    }
    // check if any prompt is pending, if not load the session again
    if (
      res.prompts.filter(
        (p) => p.status === ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING,
      ).length === 0
    ) {
      setCurrentSession((prev) => {
        if (!prev) return null;
        const updated = {
          ...prev,
          prompts: res.prompts,
        };
        currentSessionRef.current = updated;
        return updated;
      });
    } else {
      // Mark the AI message as no longer streaming
      if (currentApiAIPromptRef.current) {
        updateMessage(currentApiAIPromptRef.current, {
          status: ApiAIPromptStatus.AI_PROMPT_STATUS_COMPLETED,
        });
      }
    }

    currentApiAIPromptRef.current = null;
  }, [updateMessage]);

  // Initialize streaming hook
  const { streamPromptResult, disconnect } = useAIStreaming({
    onMessage: handleSSEMessage,
    onError: handleSSEError,
    onClose: handleSSEClose,
    autoReconnect: true,
  });

  const stopStreaming = useCallback(async () => {
    const promptId = currentApiAIPromptRef.current;
    const sessionId = currentSessionIdRef.current;

    disconnect();
    setIsStreaming(false);
    setIsConnecting(false);
    setStreamingError(null);

    if (promptId) {
      updateMessage(promptId, {
        status: ApiAIPromptStatus.AI_PROMPT_STATUS_CANCELLED,
        response: currentContentRef.current,
      });
    }

    currentApiAIPromptRef.current = null;

    if (!selectedProject?.id || !promptId || !sessionId) {
      return;
    }
    try {
      await aiService.aiServiceCancelSessionPrompt({
        projectId: selectedProject.id,
        sessionId,
        promptId,
        payload: {},
      });
    } catch (err) {
      // Do not surface cancellation race errors to users.
      console.warn("Failed to cancel prompt on backend", err);
    }
  }, [disconnect, selectedProject?.id, updateMessage]);

  const pendingPrompt = useMemo(() => {
    return currentSession?.prompts?.find(
      (prompt) => prompt.status === ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING,
    );
  }, [currentSession?.prompts]);

  const prevPendingPrompt = usePrevious(pendingPrompt);

  useEffect(() => {
    // Use ID comparison instead of object reference equality
    if (!pendingPrompt || prevPendingPrompt?.id === pendingPrompt.id) return;
    if (!currentSession?.id) return;

    currentApiAIPromptRef.current = pendingPrompt.id!;
    currentSessionIdRef.current = currentSession.id!;
    currentContentRef.current = pendingPrompt.response ?? "";

    const path = `/services/ai/v1/sessions/${selectedProject?.id}/prompts/${currentSession.id}/prompts/${pendingPrompt.id}`;
    setIsConnecting(false);
    setIsStreaming(true);
    streamPromptResult(path);
  }, [
    pendingPrompt,
    prevPendingPrompt?.id,
    currentSession?.id,
    selectedProject?.id,
    streamPromptResult,
  ]);

  // Effect to load session from URL when sessionId is present but no active session
  useEffect(() => {
    if (urlSessionId && !currentSession && aiSessions.length > 0) {
      loadSession(urlSessionId, true);
      setActive(true); // Auto-activate the AI modal when loading from URL
    }
  }, [urlSessionId, currentSession, aiSessions, loadSession]);

  // Effect to sync URL with current session
  useEffect(() => {
    if (currentSession?.id && currentSession.id !== urlSessionId) {
      setUrlSessionId(currentSession.id);
    }
  }, [currentSession?.id, urlSessionId, setUrlSessionId]);

  // Keep checkbox state synced with the active session mode.
  useEffect(() => {
    if (currentSession?.id) {
      setUseMcpAgent(!!currentSession.useMcpAgent);
    }
  }, [currentSession?.id, currentSession?.useMcpAgent]);

  const createSessionAndStream = useCallback(
    async (prompt: string): Promise<void> => {
      if (!prompt.trim() || isStreaming) {
        return;
      }

      try {
        setIsConnecting(true);
        setStreamingError(null);
        currentApiAIPromptRef.current = null;
        currentContentRef.current = "";

        // Use ref to get the current session value to avoid stale closures
        let session = currentSessionRef.current;
        if (!session) {
          // Create the actual API session first
          const res = await aiService.aiServiceCreateSession({
            projectId: selectedProject?.id!,
            payload: {
              prompt: prompt,
              useMcpAgent,
            },
          });

          if (!res.session) {
            throw new Error("Failed to create session");
          }

          session = res.session;
          setAiSessions(
            produce(aiSessions, (draft) => {
              if (session) {
                draft.push(session);
              }
            }),
          );
        } else {
          const res = await aiService.aiServiceCreateSessionPrompt({
            projectId: selectedProject?.id!,
            sessionId: session.id!,
            payload: {
              prompt: prompt,
              useMcpAgent,
            },
          });

          if (
            (res.prompts || []).filter(
              (p) => p.status === ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING,
            ).length === 0
          ) {
            throw new Error("Failed to create session prompt");
          }

          session.prompts = res.prompts;
        }
        setCurrentSession(session);
        currentSessionRef.current = session;

        if (!session?.id) {
          throw new Error("Failed to get session or prompt ID from API");
        }

        currentSessionIdRef.current = session?.id; // Set the ref
      } catch (error) {
        setIsConnecting(false);
        // commented out as errors seem to be duplicated
        // setStreamingError(error instanceof Error ? error.message : "Unknown error");

        // Add error message
        addResponse({
          userInput: "",
          response:
            "Sorry, there was an error processing your request. Please try again.",
          status: ApiAIPromptStatus.AI_PROMPT_STATUS_FAILED,
        });
      } finally {
        setCurrentQuestion(""); // Clear the input
      }
    },
    [
      isStreaming,
      generateId,
      streamPromptResult,
      addResponse,
      updateMessage,
      aiSessions,
      selectedProject?.id,
      currentSessionIdRef.current,
      useMcpAgent,
    ],
  );

  const retryPrompt = useCallback(
    (messageId?: string) => {
      updateMessage(messageId || currentApiAIPromptRef.current!, {
        status: ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING,
      });
    },
    [updateMessage],
  );

  const contextValue: AiContextType = {
    active,
    setActive,
    currentQuestion,
    setCurrentQuestion,
    aiSessions,
    currentSession,
    messages,
    loadSession,
    createSessionAndStream,
    addResponse,
    updateMessage,
    isStreaming,
    isConnecting,
    streamingError,
    stopStreaming,
    clearCurrentSession,
    hasFailedPrompt,
    retryPrompt,
    useMcpAgent,
    setUseMcpAgent,
    mcpModeLocked,
  };

  return (
    <AiContext.Provider value={contextValue}>{children}</AiContext.Provider>
  );
};
